import {
  Box,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Stack,
  Text,
  UnstyledButton
} from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContractLine } from '@/api/generated/models';
import { useListContractLines } from '@/api/generated/contracts/contracts';
import { useListUnits } from '@/api/generated/units/units';
import { useListLineAllocations } from '@/api/generated/work-structure/work-structure';
import { IconFigmaCheck, IconFigmaChevronDown, IconFigmaInfo } from '@/assets/icons';
import { FormModal, ModalFooter, SelectField, TextField } from '@/components/common';
import { findUnitLabel } from '@/features/units/unitDisplay';

interface ContractSpaceLinePlanPickerModalProps {
  opened: boolean;
  onClose: () => void;
  contractId: string;
  contractLabel?: string;
  /** Stavke koje su već u prostoru (spremljene ili dodane u nacrt). */
  existingContractLineIds: string[];
  /** Odabrane stavke s količinom idu u nacrt prostora (oznaka NOVO) — spremaju se tek s
   * "Spremi stavke" u razradi. */
  onAdd: (items: { line: ContractLine; plannedQty: number }[]) => void;
}

/** DRAFT-time razrada — "+ Dodaj stavku" u ContractSpaceBreakdown. Isti obrazac kao
 * WorkPackageItemPickerModal (post-aktivacijski picker), namjerno repliciran a ne dijeljen —
 * vidi CLAUDE.md o item-picker modalima. Razlika: nema workPackageId (razrada nema radni
 * paket), a odabrane stavke ne sprema sam nego ih vraća u nacrt razrade (onAdd). */
export const ContractSpaceLinePlanPickerModal = ({
  opened,
  onClose,
  contractId,
  contractLabel,
  existingContractLineIds,
  onAdd
}: ContractSpaceLinePlanPickerModalProps) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [quantities, setQuantities] = useState<Map<string, number>>(new Map());
  const [showUnallocated, setShowUnallocated] = useState(false);

  const { data } = useListContractLines(contractId, { query: { enabled: opened } });
  const { data: unitsData } = useListUnits();
  const { data: allocationsData } = useListLineAllocations(contractId, {
    query: { enabled: opened }
  });
  const allocatedQtyByLineId = new Map(
    (allocationsData ?? []).map((a) => [a.contractLineId, a.allocatedQty ?? 0])
  );
  const alreadyAdded = new Set(existingContractLineIds);
  const remainingQty = (line: ContractLine) =>
    (line.quantity ?? 0) - (allocatedQtyByLineId.get(line.id ?? '') ?? 0);

  // Stavke koje su već potpuno raspoređene po CIJELOM ugovoru (u bilo kojem prostoru) nema
  // smisla nuditi ovdje — razrada = ugovor za obje vrste ugovora (vidi
  // WorkStructureService.assertWithinRemainingQty).
  const pickableLines: ContractLine[] = (data ?? []).filter(
    (line) => !(line.id && alreadyAdded.has(line.id)) && remainingQty(line) > 0
  );
  // BUG-02 (TEST16): ista poruka o neraspoređenim pozicijama na SVAKOM prostoru — uključuje i
  // pozicije koje su već u ovom prostoru (picker ih ne nudi jer se količina mijenja u tablici).
  const unallocatedLines = (data ?? []).filter((line) => remainingQty(line) > 0);
  const formatQty = (qty: number) => String(Math.round(qty * 100) / 100);
  // Kratka oznaka jedinice (kom, m) kao u primjeru iz checkliste; puni naziv samo kao fallback.
  const unitLabel = (line: ContractLine) =>
    unitsData?.find((unit) => unit.id === line.unitId)?.code ??
    findUnitLabel(unitsData, line.unitId, i18n.language);
  const inThisSpaceNote = (line: ContractLine) => line.id && alreadyAdded.has(line.id);

  const categories = Array.from(
    new Set(pickableLines.map((line) => line.category).filter(Boolean))
  ) as string[];

  const query = search.trim().toLowerCase();
  const lines: ContractLine[] = pickableLines.filter((line) => {
    if (categoryFilter && line.category !== categoryFilter) return false;
    if (!query) return true;
    return (
      line.itemCode?.toLowerCase().includes(query) ||
      line.description?.toLowerCase().includes(query)
    );
  });

  const selectableIds = lines.map((line) => line.id).filter((id): id is string => !!id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id));
  const maxQtyById = new Map(lines.map((line) => [line.id, remainingQty(line)]));

  const toggle = (id?: string) => {
    if (!id) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setQuantities((prev) => {
      if (prev.has(id)) return prev;
      const next = new Map(prev);
      next.set(id, 0);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
    setQuantities((prev) => {
      const next = new Map(prev);
      if (!allSelected) {
        selectableIds.forEach((id) => {
          if (!next.has(id)) next.set(id, 0);
        });
      }
      return next;
    });
  };

  const setQuantity = (id: string, value: number) => {
    setQuantities((prev) => {
      const next = new Map(prev);
      next.set(id, value);
      return next;
    });
  };

  const reset = () => {
    setSearch('');
    setCategoryFilter(null);
    setSelected(new Set());
    setQuantities(new Map());
    setShowUnallocated(false);
  };

  // Sigurnosna kočnica uz max/clamp na samom polju — razrada ne smije premašiti ugovorenu
  // količinu ni za FRAMEWORK (vidi WorkStructureService).
  const hasQuantityOverContracted = Array.from(selected).some((id) => {
    const max = maxQtyById.get(id);
    return max != null && (quantities.get(id) ?? 0) > max;
  });

  // BUG-01: količina kreće od 0 — korisnik je mora sam upisati prije dodavanja.
  const hasMissingQuantity = Array.from(selected).some((id) => (quantities.get(id) ?? 0) <= 0);

  const handleAdd = () => {
    onAdd(
      lines
        .filter((line) => !!line.id && selected.has(line.id))
        .map((line) => {
          const requestedQty = quantities.get(line.id ?? '') ?? 0;
          const max = maxQtyById.get(line.id);
          return {
            line,
            plannedQty: max == null ? requestedQty : Math.min(requestedQty, Math.max(0, max))
          };
        })
    );
    reset();
    onClose();
  };

  const unallocatedBanner = !data ? null : unallocatedLines.length === 0 ? (
    <Group
      gap={8}
      wrap="nowrap"
      px={14}
      py={10}
      style={{
        borderRadius: 8,
        border: '1px solid var(--color-status-success)',
        backgroundColor: 'var(--color-status-success-soft)',
        color: 'var(--color-status-success)'
      }}
    >
      <IconFigmaCheck size={16} />
      <Text fz={13} fw={500} style={{ color: 'var(--color-text-primary)' }}>
        {t('contractSpaceBreakdown.picker.allAllocated')}
      </Text>
    </Group>
  ) : (
    <Stack
      gap={8}
      px={14}
      py={10}
      style={{
        borderRadius: 8,
        border: '1px solid var(--color-status-info)',
        backgroundColor: 'var(--color-status-info-soft)'
      }}
    >
      <Group gap={8} wrap="nowrap" align="flex-start">
        <Box mt={2} style={{ color: 'var(--color-status-info)', display: 'flex' }}>
          <IconFigmaInfo size={16} />
        </Box>
        <Text fz={13} fw={500} style={{ flex: 1, color: 'var(--color-text-primary)' }}>
          {unallocatedLines.length === 1
            ? t('contractSpaceBreakdown.picker.unallocatedOne', {
                contract: contractLabel,
                code: unallocatedLines[0].itemCode,
                qty: formatQty(remainingQty(unallocatedLines[0])),
                unit: unitLabel(unallocatedLines[0])
              }) +
              (inThisSpaceNote(unallocatedLines[0])
                ? ` ${t('contractSpaceBreakdown.picker.alreadyInSpaceHint')}`
                : '')
            : t('contractSpaceBreakdown.picker.unallocatedMany', {
                contract: contractLabel,
                count: unallocatedLines.length
              })}
        </Text>
        {unallocatedLines.length > 1 && (
          <UnstyledButton
            onClick={() => setShowUnallocated((prev) => !prev)}
            style={{ color: 'var(--color-status-info)', flexShrink: 0 }}
          >
            <Group gap={4} wrap="nowrap">
              <Text fz={13} fw={600} style={{ color: 'inherit' }}>
                {t(
                  showUnallocated
                    ? 'contractSpaceBreakdown.picker.hideUnallocated'
                    : 'contractSpaceBreakdown.picker.showUnallocated'
                )}
              </Text>
              <Box
                style={{
                  display: 'flex',
                  transform: showUnallocated ? 'rotate(180deg)' : undefined
                }}
              >
                <IconFigmaChevronDown size={14} />
              </Box>
            </Group>
          </UnstyledButton>
        )}
      </Group>
      {unallocatedLines.length > 1 && showUnallocated && (
        <Stack gap={0} pl={24} style={{ maxHeight: 150, overflowY: 'auto' }}>
          {unallocatedLines.map((line) => (
            <Group key={line.id} gap={12} wrap="nowrap" py={4}>
              <Text fz={13} fw={600} w={120} style={{ color: 'var(--color-text-primary)' }}>
                {t('contractSpaceBreakdown.picker.positionLabel', { code: line.itemCode })}
              </Text>
              <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                {t('contractSpaceBreakdown.picker.remainingLabel', {
                  qty: formatQty(remainingQty(line)),
                  unit: unitLabel(line)
                })}
              </Text>
              {inThisSpaceNote(line) && (
                <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                  · {t('contractSpaceBreakdown.picker.alreadyInSpace')}
                </Text>
              )}
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      size={860}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('contractSpaceBreakdown.picker.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        {unallocatedBanner}
        {pickableLines.length === 0 ? (
          (data ?? []).length === 0 && (
            <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
              {t('workPackages.picker.emptyState')}
            </Text>
          )
        ) : (
          <>
            <Group gap={12} wrap="wrap">
              <div style={{ flex: 1, minWidth: 220 }}>
                <TextField
                  hideLabel
                  placeholder={t('workPackages.picker.searchPlaceholder')}
                  value={search}
                  onChange={(event) => setSearch(event.currentTarget.value)}
                />
              </div>
              <div style={{ width: 200 }}>
                <SelectField
                  hideLabel
                  placeholder={t('workPackages.picker.allCategories')}
                  clearable
                  data={[
                    { value: '', label: t('workPackages.picker.allCategories') },
                    ...categories.map((c) => ({ value: c, label: c }))
                  ]}
                  value={categoryFilter ?? ''}
                  onChange={(value) => setCategoryFilter(value || null)}
                />
              </div>
            </Group>

            <Stack gap={0} style={{ maxHeight: 360, overflowY: 'auto' }}>
              <Group
                gap={8}
                wrap="nowrap"
                py={4}
                style={{ borderBottom: '1px solid var(--color-border-soft)' }}
              >
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  disabled={selectableIds.length === 0}
                  onChange={toggleAll}
                  aria-label={t('workPackages.picker.selectAll')}
                />
                <Text fz={11} fw={600} w={90} style={{ color: 'var(--color-text-muted)' }}>
                  {t('workPackages.picker.colCode')}
                </Text>
                <Text fz={11} fw={600} style={{ flex: 1, color: 'var(--color-text-muted)' }}>
                  {t('workPackages.picker.colName')}
                </Text>
                <Text fz={11} fw={600} w={70} style={{ color: 'var(--color-text-muted)' }}>
                  {t('workPackages.picker.colUnit')}
                </Text>
                <Text
                  fz={11}
                  fw={600}
                  w={100}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {t('workPackages.picker.colContracted')}
                </Text>
                <Text
                  fz={11}
                  fw={600}
                  w={85}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {t('workPackages.picker.colQty')}
                </Text>
              </Group>

              {lines.length === 0 && (
                <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
                  {t('workPackages.picker.emptyState')}
                </Text>
              )}

              {lines.map((line, index) => {
                const isSelected = !!line.id && selected.has(line.id);
                const isOverLimit =
                  isSelected && (quantities.get(line.id ?? '') ?? 0) > remainingQty(line);
                const isMissingQty = isSelected && (quantities.get(line.id ?? '') ?? 0) <= 0;
                return (
                  <Group
                    key={line.id}
                    gap={8}
                    wrap="nowrap"
                    py={10}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggle(line.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') toggle(line.id);
                    }}
                    style={{
                      cursor: 'pointer',
                      borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggle(line.id)}
                      onClick={(event) => event.stopPropagation()}
                    />
                    <Text fz={13} fw={600} w={90} style={{ color: 'var(--color-text-primary)' }}>
                      {line.itemCode}
                    </Text>
                    <Text
                      fz={13}
                      style={{ flex: 1, color: 'var(--color-text-secondary)' }}
                      truncate
                    >
                      {line.description}
                    </Text>
                    <Text fz={13} w={70} style={{ color: 'var(--color-text-muted)' }}>
                      {findUnitLabel(unitsData, line.unitId, i18n.language)}
                    </Text>
                    <Text
                      fz={13}
                      w={100}
                      ta="right"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {(allocatedQtyByLineId.get(line.id ?? '') ?? 0) +
                        (isSelected ? (quantities.get(line.id ?? '') ?? 0) : 0)}
                      {' / '}
                      {line.quantity}
                    </Text>
                    <Box w={85} onClick={(event) => event.stopPropagation()}>
                      <NumberInput
                        size="xs"
                        hideControls
                        min={0}
                        step={1}
                        error={isOverLimit || isMissingQty}
                        disabled={!isSelected}
                        value={line.id ? (quantities.get(line.id) ?? 0) : 0}
                        onChange={(value) => {
                          if (!line.id) return;
                          setQuantity(line.id, Math.max(0, Math.trunc(Number(value)) || 0));
                        }}
                      />
                    </Box>
                  </Group>
                );
              })}
            </Stack>
          </>
        )}

        <ModalFooter>
          {pickableLines.length === 0 ? (
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
            >
              {t('common.ok')}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  reset();
                  onClose();
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                disabled={selected.size === 0 || hasQuantityOverContracted || hasMissingQuantity}
                onClick={handleAdd}
              >
                {t('workPackages.picker.addSelected', { count: selected.size })}
              </Button>
            </>
          )}
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
